/**
 * Visually position Splide pagination for carousel-pagination-in-slide carousels.
 *
 * Pagination stays in Splide's default DOM position (sibling of .splide__track).
 * All slides are measured once (tallest media / longest content) so dots stay
 * fixed while cycling. 991px and below: between media and content. 992px–1229px:
 * below content column. 1230px+: cleared; carousel-autoplay SCSS handles pagination.
 *
 * @since 1.0.0
 */
import { alignCarouselAutoplayToggle } from './carousel-autoplay-toggle.js';

const mobileMediaQuery = '(max-width: 991px)';
const mediumMediaQuery = '(min-width: 992px) and (max-width: 1229.98px)';
const mobilePaginationGapPx = 16;
const mobilePaginationTopOffsetPx = 140;
const tabletPaginationGapPx = 8;
const carouselSelector = '.carousel-pagination-in-slide';
const hiddenPanelClass = 'is-carousel-panel-hidden';
const resizeDebounceMs = 150;

const resizeObservers = new WeakMap();
const carouselSizes = new WeakMap();

/**
 * @returns {'mobile'|'medium'|null}
 */
function getLayoutMode() {
	if (window.matchMedia(mobileMediaQuery).matches) {
		return 'mobile';
	}

	if (window.matchMedia(mediumMediaQuery).matches) {
		return 'medium';
	}

	return null;
}

/**
 * @param {Element} ancestor
 * @param {Element} element
 * @returns {{ top: number, left: number, bottom: number, width: number }|null}
 */
function getOffsetWithinAncestor(ancestor, element) {
	if (!ancestor.contains(element)) {
		return null;
	}

	let top = 0;
	let left = 0;
	let current = element;

	while (current && current !== ancestor) {
		top += current.offsetTop;
		left += current.offsetLeft;
		current = current.parentElement;
	}

	if (current !== ancestor) {
		return null;
	}

	return {
		top,
		left,
		bottom: top + element.offsetHeight,
		width: element.offsetWidth,
	};
}

/**
 * @param {Element} carousel
 * @returns {{ maxMediaBottom: number, maxContentBottom: number, contentLeft: number|null, contentWidth: number|null }}
 */
function measureSlides(carousel) {
	const metrics = {
		maxMediaBottom: 0,
		maxContentBottom: 0,
		contentLeft: null,
		contentWidth: null,
	};

	carousel.querySelectorAll('.splide__slide').forEach((slide) => {
		const media = slide.querySelector('.wp-block-media-text__media');
		const content = slide.querySelector('.wp-block-media-text__content');

		if (media) {
			const box = getOffsetWithinAncestor(slide, media);

			if (box) {
				metrics.maxMediaBottom = Math.max(metrics.maxMediaBottom, box.bottom);
			}
		}

		if (content) {
			const box = getOffsetWithinAncestor(slide, content);

			if (box) {
				metrics.maxContentBottom = Math.max(metrics.maxContentBottom, box.bottom);

				if (metrics.contentLeft === null) {
					metrics.contentLeft = box.left;
					metrics.contentWidth = box.width;
				}
			}
		}
	});

	return metrics;
}

/**
 * @param {Element} carousel
 * @param {Element} track
 * @returns {{ trackTop: number, trackLeft: number }}
 */
function measureTrackOffset(carousel, track) {
	const carouselRect = carousel.getBoundingClientRect();
	const trackRect = track.getBoundingClientRect();

	return {
		trackTop: trackRect.top - carouselRect.top,
		trackLeft: trackRect.left - carouselRect.left,
	};
}

/**
 * @param {'mobile'|'medium'} mode
 * @param {{ maxMediaBottom: number, maxContentBottom: number, contentLeft: number|null, contentWidth: number|null }} metrics
 * @param {{ trackTop: number, trackLeft: number }} trackOffset
 * @returns {{ top: string, left?: string, width?: string }|null}
 */
function buildPaginationVars(mode, metrics, trackOffset) {
	if (mode === 'mobile') {
		if (metrics.maxMediaBottom === 0) {
			return null;
		}

		return {
			top: `${trackOffset.trackTop + metrics.maxMediaBottom + mobilePaginationGapPx + mobilePaginationTopOffsetPx}px`,
		};
	}

	if (
		metrics.maxContentBottom === 0 ||
		metrics.contentLeft === null ||
		metrics.contentWidth === null
	) {
		return null;
	}

	return {
		top: `${trackOffset.trackTop + metrics.maxContentBottom + tabletPaginationGapPx}px`,
		left: `${trackOffset.trackLeft + metrics.contentLeft}px`,
		width: `${metrics.contentWidth}px`,
	};
}

/**
 * @param {Element} carousel
 */
function disconnectResizeObserver(carousel) {
	const observer = resizeObservers.get(carousel);

	if (observer) {
		observer.disconnect();
		resizeObservers.delete(carousel);
	}
}

/**
 * @param {Element} carousel
 */
function observeTrackResize(carousel) {
	disconnectResizeObserver(carousel);

	const track = carousel.querySelector('.splide__track');

	if (!track || typeof window.ResizeObserver === 'undefined') {
		return;
	}

	const observer = new window.ResizeObserver(() => {
		positionCarousel(carousel);
	});

	observer.observe(track);
	resizeObservers.set(carousel, observer);
}

/**
 * @param {Element} carousel
 */
function clearPaginationPosition(carousel) {
	disconnectResizeObserver(carousel);
	carousel.classList.remove('is-pagination-positioned');
	carousel.style.removeProperty('--carousel-pagination-top');
	carousel.style.removeProperty('--carousel-pagination-offset');
	carousel.style.removeProperty('--carousel-pagination-left');
	carousel.style.removeProperty('--carousel-pagination-width');
}

/**
 * @param {Element} carousel
 * @param {{ top: string, left?: string, width?: string }} vars
 */
function applyPaginationVars(carousel, vars) {
	carousel.style.setProperty('--carousel-pagination-top', vars.top);
	carousel.style.removeProperty('--carousel-pagination-offset');

	if (vars.left) {
		carousel.style.setProperty('--carousel-pagination-left', vars.left);
	} else {
		carousel.style.removeProperty('--carousel-pagination-left');
	}

	if (vars.width) {
		carousel.style.setProperty('--carousel-pagination-width', vars.width);
	} else {
		carousel.style.removeProperty('--carousel-pagination-width');
	}

	carousel.classList.add('is-pagination-positioned');
	observeTrackResize(carousel);
}

/**
 * @param {Element} carousel
 * @returns {boolean}
 */
function isCarouselMeasurable(carousel) {
	if (carousel.hasAttribute('hidden') || carousel.classList.contains(hiddenPanelClass)) {
		return false;
	}

	return carousel.offsetParent !== null;
}

/**
 * @param {Element} carousel
 */
function scheduleToggleAlign(carousel) {
	const scope =
		carousel.closest('.carousel-autoplay') ||
		(carousel.classList.contains('carousel-autoplay') ? carousel : null);

	if (scope) {
		alignCarouselAutoplayToggle(scope);
	}
}

/**
 * @param {Element} carousel
 */
function positionCarousel(carousel) {
	if (!isCarouselMeasurable(carousel)) {
		clearPaginationPosition(carousel);
		scheduleToggleAlign(carousel);
		return;
	}

	const pagination = carousel.querySelector('.splide__pagination');
	const track = carousel.querySelector('.splide__track');
	const mode = getLayoutMode();

	if (!pagination || !track || !mode) {
		clearPaginationPosition(carousel);
		scheduleToggleAlign(carousel);
		return;
	}

	const vars = buildPaginationVars(
		mode,
		measureSlides(carousel),
		measureTrackOffset(carousel, track)
	);

	if (!vars) {
		clearPaginationPosition(carousel);
		scheduleToggleAlign(carousel);
		return;
	}

	applyPaginationVars(carousel, vars);
	scheduleToggleAlign(carousel);
}

/**
 * @param {Element|null} carousel
 */
function schedulePosition(carousel) {
	const run = () => {
		if (carousel) {
			positionCarousel(carousel);
			return;
		}

		document.querySelectorAll(carouselSelector).forEach(positionCarousel);
	};

	requestAnimationFrame(run);
}

/**
 * @param {Element} carousel
 */
function handleSplideResized(carousel) {
	const rect = carousel.getBoundingClientRect();
	const previous = carouselSizes.get(carousel);

	if (previous && previous.width === rect.width && previous.height === rect.height) {
		return;
	}

	carouselSizes.set(carousel, { width: rect.width, height: rect.height });
	schedulePosition(carousel);
}

export function initCarouselPaginationInSlide() {
	const mobileMediaQueryList = window.matchMedia(mobileMediaQuery);
	const mediumMediaQueryList = window.matchMedia(mediumMediaQuery);

	schedulePosition(null);

	let resizeTimeout;
	window.addEventListener('resize', () => {
		clearTimeout(resizeTimeout);
		resizeTimeout = setTimeout(() => schedulePosition(null), resizeDebounceMs);
	});

	mobileMediaQueryList.addEventListener('change', () => schedulePosition(null));
	mediumMediaQueryList.addEventListener('change', () => schedulePosition(null));

	document.addEventListener('splide:mounted', (e) => {
		const carousel = e.target.closest(carouselSelector);
		if (carousel) {
			schedulePosition(carousel);
		}
	});

	document.addEventListener('splide:resized', (e) => {
		const carousel = e.target.closest(carouselSelector);
		if (carousel) {
			handleSplideResized(carousel);
		}
	});

	window.addEventListener('load', () => schedulePosition(null));
}
